import React, { useState } from 'react';
import { User, Mail, Camera, Save, CreditCard, AlertTriangle, ExternalLink, Keyboard, ChevronDown, ChevronRight, Share2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import { useUserPlan } from '../hooks/useUserPlan';
import ConfirmationModal from './ConfirmationModal';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import ReferralDashboard from './ReferralDashboard';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSubscription: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ isOpen, onClose, onOpenSubscription }) => {
  const { currentUser, userProfile, updateUserProfile, refreshSubscriptionStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const { planData } = useUserPlan();
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const { shortcuts } = useKeyboardShortcuts();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
  });

  // Update form data when modal opens or user data changes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        displayName: userProfile?.displayName || currentUser?.displayName || '',
        email: currentUser?.email || '',
      });
    }
  }, [isOpen, userProfile, currentUser]);

  // Use cached subscription data from AuthContext instead of fetching
  const subscriptionInfo = planData ? {
    plan: planData.plan,
    subscription_status: planData.subscription_status,
    has_active_subscription: planData.has_active_subscription,
    cancel_at_period_end: planData.cancel_at_period_end,
    current_period_end: planData.current_period_end,
    subscription_start_date: userProfile?.createdAt?.toISOString()
  } : null;

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (userProfile && formData.displayName !== userProfile.displayName) {
        await updateUserProfile({
          displayName: formData.displayName
        });
      }
      
      toast.success('Settings saved successfully!');
      onClose();
    } catch (error) {
      console.error('Settings save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentUser) {
      toast.error('Please sign in to cancel your subscription');
      return;
    }

    try {
      setCanceling(true);
      
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to cancel subscription';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      toast.success(result.message || 'Subscription canceled successfully');
      
      // Refresh subscription info
      await refreshSubscriptionStatus();
      setShowCancelConfirm(false);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCanceling(false);
    }
  };

  const handleManageBilling = async () => {
    if (!currentUser) {
      toast.error('Please sign in to manage billing');
      return;
    }

    try {
      setOpeningBillingPortal(true);
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle the case where user doesn't have a Stripe customer ID
        if (response.status === 400) {
          if (errorData.detail?.includes('Stripe customer ID')) {
            toast.info('You need an active subscription to access billing management. Please upgrade first.');
            return;
          }
          toast.error(errorData.detail || 'Unable to access billing portal');
          return;
        }
        
        throw new Error(errorData.detail || 'Failed to create portal session');
      }

      const data = await response.json();
      
      // Open portal in new tab
      if (data.portal_url) {
        window.open(data.portal_url, '_blank');
      } else {
        throw new Error('No portal URL received');
      }
      
    } catch (error) {
      console.error('Error opening billing portal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to open billing portal. Please try again.';
      toast.error(errorMessage);
    } finally {
      setOpeningBillingPortal(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleProfilePictureUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setLoading(true);
          
          // Validate file size (max 5MB)
          if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
          }

          // Validate file type
          if (!file.type.startsWith('image/')) {
            toast.error('Please select a valid image file');
            return;
          }

          // Create a unique filename
          const fileExtension = file.name.split('.').pop();
          const fileName = `profile-pictures/${currentUser?.uid}-${Date.now()}.${fileExtension}`;

          // Upload to Firebase Storage
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const storage = getStorage();
          const storageRef = ref(storage, fileName);
          
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);

          // Update user profile with new photo URL
          await updateUserProfile({
            photoURL: downloadURL
          });

          toast.success('Profile picture updated successfully!');
        } catch (error) {
          console.error('Error uploading profile picture:', error);
          toast.error('Failed to upload profile picture. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };
    input.click();
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Account Settings" size="lg">
      <div className="space-y-6">
        {/* Profile Picture */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={32} className="text-primary-400" />
              )}
            </div>
            <button 
              onClick={handleProfilePictureUpload}
              className="absolute bottom-0 right-0 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-primary-800 transition-colors"
            >
              <Camera size={12} />
            </button>
          </div>
          <div>
            <h3 className="font-medium">Profile Picture</h3>
            <p className="text-sm text-primary-600">Update your avatar</p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <label className="label">Display Name</label>
            <div className="relative">
              <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className="input-field pl-11"
                placeholder="Enter your display name"
              />
            </div>
          </div>

          <div>
            <label className="label">Email</label>
            <div className="relative">
              <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
              <input
                type="email"
                value={formData.email}
                className="input-field pl-11 bg-primary-50 cursor-not-allowed"
                disabled
              />
            </div>
            <p className="text-xs text-primary-500 mt-1">
              Email cannot be changed. Contact support if needed.
            </p>
          </div>
        </div>

        {/* Subscription Management */}
        {subscriptionInfo && (
          <div className="bg-primary-50 rounded-xl p-6 space-y-4">
            <div className="flex items-center space-x-2">
              <CreditCard size={20} className="text-primary-600" />
              <h3 className="font-medium">Subscription Management</h3>
            </div>

            {/* Current Plan */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-primary-600">Current Plan</p>
                <p className="font-medium capitalize">{subscriptionInfo.plan}</p>
              </div>
              <div>
                <p className="text-sm text-primary-600">Status</p>
                <p className={`font-medium ${
                  subscriptionInfo.has_active_subscription ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {subscriptionInfo.has_active_subscription ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>

            {/* Subscription Details */}
            {subscriptionInfo.has_active_subscription && (
              <>
                {subscriptionInfo.subscription_start_date && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-primary-600">Started</p>
                      <p className="font-medium">{formatDate(subscriptionInfo.subscription_start_date)}</p>
                    </div>
                    {subscriptionInfo.current_period_end && (
                      <div>
                        <p className="text-sm text-primary-600">
                          {subscriptionInfo.cancel_at_period_end ? 'Expires' : 'Renews'}
                        </p>
                        <p className="font-medium">{formatDate(subscriptionInfo.current_period_end)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancellation Warning */}
                {subscriptionInfo.cancel_at_period_end && (
                  <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle size={16} className="text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Subscription Canceled</p>
                      <p className="text-xs text-red-700">
                        Your subscription will end on {subscriptionInfo.current_period_end ? formatDate(subscriptionInfo.current_period_end) : 'the end of your billing period'}. 
                        You'll keep access until then.
                      </p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={handleManageBilling}
                    disabled={openingBillingPortal}
                    className="flex items-center space-x-2 px-4 py-2 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {openingBillingPortal ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ExternalLink size={16} />
                    )}
                    <span>{openingBillingPortal ? 'Opening Portal...' : 'Manage Billing'}</span>
                  </button>

                  {!subscriptionInfo.cancel_at_period_end && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={canceling}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {canceling ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <AlertTriangle size={16} />
                      )}
                      <span>{canceling ? 'Canceling...' : 'Cancel Subscription'}</span>
                    </button>
                  )}
                </div>
                
                {/* Billing Portal Info */}
                <div className="text-xs text-gray-500 mt-2">
                  Billing portal: Update payment methods, view invoices, manage billing
                </div>
              </>
            )}

            {/* Free Plan Info */}
            {!subscriptionInfo.has_active_subscription && (
              <div className="text-center py-4">
                <p className="text-sm text-primary-600 mb-3">
                  You're currently on the free plan with limited features.
                </p>
                <button 
                  onClick={onOpenSubscription}
                  className="btn-primary text-sm"
                >
                  Upgrade to Pro
                </button>
              </div>
            )}
          </div>
        )}

        {/* Referral Program */}
        <div className="bg-primary-50 rounded-xl p-6">
          <button
            onClick={() => setShowReferrals(!showReferrals)}
            className="flex items-center justify-between w-full text-left hover:bg-primary-100 -m-2 p-2 rounded-lg transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Share2 size={20} className="text-primary-600" />
              <h3 className="font-medium">Referral Program</h3>
            </div>
            {showReferrals ? (
              <ChevronDown size={20} className="text-primary-600" />
            ) : (
              <ChevronRight size={20} className="text-primary-600" />
            )}
          </button>
          
          {showReferrals && (
            <div className="mt-4">
              <ReferralDashboard />
            </div>
          )}
        </div>

        {/* Keyboard Shortcuts */}
        <div className="bg-primary-50 rounded-xl p-6">
          <button
            onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
            className="flex items-center justify-between w-full text-left hover:bg-primary-100 -m-2 p-2 rounded-lg transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Keyboard size={20} className="text-primary-600" />
              <h3 className="font-medium">Keyboard Shortcuts</h3>
            </div>
            {showKeyboardShortcuts ? (
              <ChevronDown size={20} className="text-primary-600" />
            ) : (
              <ChevronRight size={20} className="text-primary-600" />
            )}
          </button>
          
          {showKeyboardShortcuts && (
            <div className="mt-4 space-y-3">
              {shortcuts.map((shortcut, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-primary-600">{shortcut.description}</span>
                  <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded shadow-sm">
                    {shortcut.key}
                  </kbd>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-primary-200 text-xs text-primary-500">
                <p>💡 Shortcuts work when not typing in input fields</p>
              </div>
            </div>
          )}
        </div>

        {/* Legal Links */}
        <div className="bg-primary-50 rounded-xl p-6">
          <h3 className="font-medium mb-4">Legal & Privacy</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-800 transition-colors"
            >
              <ExternalLink size={14} />
              <span>Privacy Policy</span>
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-800 transition-colors"
            >
              <ExternalLink size={14} />
              <span>Terms of Service</span>
            </a>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-primary-100">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="btn-primary flex items-center"
          >
            {loading ? (
              <span className="loading-dots mr-2">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : (
              <Save size={16} className="mr-2" />
            )}
            Save Changes
          </button>
        </div>

        {/* Cancel Subscription Confirmation Modal */}
        <ConfirmationModal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
          title="Cancel Subscription"
          message="Are you sure you want to cancel your subscription? You will keep access until the end of your billing period, but won't be charged again."
          confirmText="Cancel Subscription"
          cancelText="Keep Subscription"
          type="danger"
          icon={<AlertTriangle size={24} />}
          isLoading={canceling}
        />
      </div>
    </Modal>
  );
};

export default UserSettingsModal; 